export type RaceClockServerSample = {
  protocolVersion: number;
  serverReceiveTimeMillis: number;
  serverSendTimeMillis: number;
};

type OffsetSample = { offsetMillis: number; roundTripMillis: number };

const OFFSET_KEY = "rauxa.raceClock.serverOffsetMillis";

class RaceClock {
  private offsetMillis = Number(globalThis.localStorage?.getItem(OFFSET_KEY) || 0) || 0;
  private anchorEpochMillis = Date.now() + this.offsetMillis;
  private anchorMonotonicMillis = performance.now();
  private synchronization: Promise<void> | null = null;
  private lastSynchronizationAttempt = 0;

  nowMillis = (): number =>
    this.anchorEpochMillis + performance.now() - this.anchorMonotonicMillis;

  now = (): Date => new Date(this.nowMillis());

  reanchor = (): void => {
    this.anchorEpochMillis = Date.now() + this.offsetMillis;
    this.anchorMonotonicMillis = performance.now();
  };

  synchronize(fetchSample: () => Promise<RaceClockServerSample>): Promise<void> {
    if (this.synchronization) return this.synchronization;
    if (Date.now() - this.lastSynchronizationAttempt < 60_000) {
      return Promise.resolve();
    }
    this.lastSynchronizationAttempt = Date.now();
    this.synchronization = this.collectSamples(fetchSample).finally(() => {
      this.synchronization = null;
    });
    return this.synchronization;
  }

  private async collectSamples(
    fetchSample: () => Promise<RaceClockServerSample>
  ): Promise<void> {
    const samples: OffsetSample[] = [];
    for (let index = 0; index < 3; index += 1) {
      const clientSendTimeMillis = Date.now();
      const sendMonotonicMillis = performance.now();
      try {
        const server = await fetchSample();
        const clientReceiveTimeMillis = Date.now();
        samples.push({
          roundTripMillis: Math.max(0, performance.now() - sendMonotonicMillis),
          offsetMillis:
            ((server.serverReceiveTimeMillis - clientSendTimeMillis) +
              (server.serverSendTimeMillis - clientReceiveTimeMillis)) /
            2,
        });
      } catch {
        // A cached offset is deliberately retained for offline race-day use.
      }
    }
    const best = samples.sort((a, b) => a.roundTripMillis - b.roundTripMillis)[0];
    if (best) {
      this.offsetMillis = best.offsetMillis;
      globalThis.localStorage?.setItem(OFFSET_KEY, String(this.offsetMillis));
    }
    this.reanchor();
  }
}

export const raceClock = new RaceClock();
